import { makeStyles } from 'design-system';
import { Icon, Tabs } from '@scale/scaleui';
import cn from 'classnames';
import { TabType } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/types';
import { twMerge } from 'tailwind-merge';

interface CodeEditorExecutionOutputProps {
  className?: string;
  outputValue: string;
  setOutputTabOpen: (value: boolean) => void;
  tab: TabType;
  setTab: (value: TabType) => void;
  fetchError: boolean;
  fullScreenEnabled: boolean;
  fullWidthEnabled: boolean;
}

const EXECUTION_SUBMISSION_ERROR =
  'Error executing code - this usually occurs due to execution timeout. Please try again.';

function CodeEditorExecutionOutput({
  className,
  outputValue,
  setOutputTabOpen,
  tab,
  setTab,
  fetchError,
  fullScreenEnabled,
  fullWidthEnabled,
}: CodeEditorExecutionOutputProps) {
  const classes = useStyles();

  return (
    <div
      className={twMerge(
        cn(
          'flex flex-col',
          {
            'pl-4': fullScreenEnabled && !fullWidthEnabled,
            'pl-2 w-3/12': !fullScreenEnabled && !fullWidthEnabled,
          },
          className,
        ),
      )}
    >
      <div
        className={cn('flex flex-row rounded-t-lg h-16 justify-between bg-[#2d2d30]', {
          'w-full': !fullScreenEnabled,
          'w-[23vw]': fullScreenEnabled,
        })}
      >
        <Tabs
          className="pt-2 pl-3 border-none"
          value={tab}
          tabs={[
            {
              id: TabType.STDOUT,
              label: 'Output',
            },
            {
              id: TabType.STDERR,
              label: 'Errors',
            },
          ]}
          onChange={tabId => setTab(tabId as TabType)}
        />
        <div className="pt-4 pr-2">
          <Icon
            icon="xmark"
            variant="white"
            onClick={e => {
              e.stopPropagation();
              setOutputTabOpen(false);
            }}
            tooltipProps={{
              side: 'top',
            }}
            tooltip="Hide Output/Errors"
          />
        </div>
      </div>
      <div
        className={cn('flex flex-row rounded-b-lg bg-[#1e1e1e]', {
          'h-[80vh] w-[23vw] z-[10002]': fullScreenEnabled,
          'h-64 w-full': !fullScreenEnabled,
        })}
      >
        <div
          className={cn(
            'text-sm p-2 pl-3 overscroll-x-contain overflow-y-scroll font-mono w-full whitespace-pre-wrap',
            classes.scrollBarColor,
            {
              'text-neutral-0': tab === TabType.STDOUT,
              'text-danger-400': tab === TabType.STDERR || fetchError,
            },
          )}
        >
          {fetchError ? EXECUTION_SUBMISSION_ERROR : outputValue}
        </div>
      </div>
    </div>
  );
}

const useStyles = makeStyles(() => ({
  scrollBarColor: {
    scrollbarColor: 'rgba(255, 255, 255, 0.5) rgba(255, 255, 255, 0.1)',
    scrollbarWidth: 'thin',
  },
}));

export default CodeEditorExecutionOutput;
